/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';

import { UserStatus } from './user-status.enum';

export const UserAllowAccess = createEnum('USER_ALLOW_ACCESS', {
  OwnLeads: 'OWN_LEADS',
  SalespersonLeads: 'SALESPERSON_LEADS',
  AllLeads: 'ALL_LEADS',
});

export type UserAllowAccess = EnumSet<typeof UserAllowAccess>;

export type UserDetailsContactDto = {
  id: ID;
  name: string;
  firstName?: string;
  lastName?: string;
};

export type UserDetailsSalesPersonDto = {
  userId: string;
  userName: string;
  userEmail?: string;
  systemRoles: Hash<boolean>;
  officeRole: any;
  status: UserStatus;
  statusValue: UserStatus;
};

export type UserDataToTransfer = {
  userId: string;
  userName: string;
};

export type UserValidateLoginExistingUserDto = {
  userId: ID;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  homeOfficeId?: ID;
  homeOfficeName?: string;
  /** Office A roles — Hash map and/or list of role codes depending on BE. */
  homeSystemRoles?: Hash<boolean> | string[];
};

export type UserValidateLoginDto = {
  valid: boolean;
  transferred?: boolean;
  /** True when login exists in another office and can be granted here. */
  existingUser?: boolean;
  user?: UserValidateLoginExistingUserDto;
};

export type UserDetailsFilterDto = {
  name?: string;
  id?: ID;
  autocomplete?: boolean;
  showInactive?: boolean;
};

export type UserPositionDto = {
  code: string;
  itemCode: string;
  localization: string;
};

export type UserDetailsDto = WithOptimisticLock<WithPrivilegedToken<{
  status: UserStatus;
  userEmail: string;
  systemRoles: Hash<boolean>;
  userId?: ID;
  transfer?: string;
  contact?: UserDetailsContactDto;
  role?: number;
  position?: UserPositionDto;
  confirmations?: any;
  allowAccessToLeads?: UserAllowAccess;
  leadsOf?: ID[];
  targetSalespersonIds?: ID[];
  useRoundRobin?: boolean;
  userToReassignLeadsTo?: string;
  allowAccessToMultipleOffices: boolean;
  /** Legacy email-routing workaround (sendEmailsFrom). Separate from membership multi-office. */
  allowEmailAccessToMultipleOffices?: boolean;
  sendEmailsFrom?: any;
  publishToWeb: boolean;
  userDataToTransfer?: string;
  hasAssignedLeads?: boolean;
  /** Created from an existing Google account ("Existing User"); persisted by the backend and returned on GET. */
  existingUser?: boolean;
}>>;

export type UserDetailsShortDto = {
  userId: ID;
  userName: string;
};

export type UserAssignedRoleDto = {
  roleAssigned: boolean;
};